import { MapInfoContext } from '../context/mapInfoContext';
import React, { useEffect, useState, useContext } from 'react';

import attraction_maker from '../assets/images/map_marker/attraction_maker.png'
import food_maker from '../assets/images/map_marker/food_maker.png'
import hotel_maker from '../assets/images/map_marker/hotel_maker.png'
import monument_maker from '../assets/images/map_marker/monument_maker.png'
import temple_maker from '../assets/images/map_marker/temple_maker.png'
import { AttractionData, LocationData } from '../types/CustomType';
import { LOCATION_ACCURACY_CONFIG } from '../configuration';
import { devLog } from '../utils/devLogger';
import {
  getCurrentUserLocation,
  getHighAccuracyUserLocation,
  getSmartUserLocation,
  getYongJingDefaultLocation,
  checkGeolocationSupport
} from '../utils/mapNavigationUtils';

// 📌 加上 Axios 發送 API 請求
import axios from 'axios';

// 永靖預設座標 (當定位失敗時使用) - 與 mapNavigationUtils.ts 保持一致
const DEFAULT_COORDINATES = { lat: 23.9238, lng: 120.5478 };

/**
 * 取得使用者當前座標並反查城市、區域（透過 Google Maps Geocoding API）
 * 整合位置權限管理系統，當權限被拒絕或未設定時會使用預設座標
 */
export const GetUserLocationInfo = async (apiKey: string): Promise<{ city: string; district: string } | null> => {
  // 內部函數：使用指定座標進行反查
  const geocodeCoordinates = async (lat: number, lng: number) => {
    try {
      const response = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json`, {
        params: {
          latlng: `${lat},${lng}`,
          key: apiKey,
          language: 'zh-TW',
        }
      });

      const results = response.data.results;
      if (!results || results.length === 0) {
        return null;
      }

      // 提取地址組件
      const addressComponents = results[0].address_components;

      let city = "";
      let district = "";

      for (const component of addressComponents) {
        if (component.types.includes("administrative_area_level_1")) {
          city = component.long_name;
        } else if (component.types.includes("administrative_area_level_2") || component.types.includes("locality") || component.types.includes("sublocality_level_1")) {
          district = component.long_name;
        }
      }

      return { city, district };
    } catch (error) {
      console.error("Geocoding API error:", error);
      return null;
    }
  };

  // LINE 內建瀏覽器的 geolocation permission dialog 不觸發 JS timeout，
  // 加上 4 秒硬性上限，逾時直接 resolve(null)，讓呼叫端用空值繼續。
  const HARD_TIMEOUT_MS = 1000;

  const locationWork = new Promise<{ city: string; district: string } | null>((resolve) => {
    devLog("🧠 GetUserLocationInfo: 使用自動定位系統");

    if (!checkGeolocationSupport()) {
      devLog("Geolocation is not supported by this browser, using default coordinates.");
      const defaultLocation = getYongJingDefaultLocation();
      geocodeCoordinates(defaultLocation.lat, defaultLocation.lng).then(resolve);
      return;
    }

    getSmartUserLocation(LOCATION_ACCURACY_CONFIG.TARGET_ACCURACY)
      .then((position) => {
        if (position) {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const accuracy = position.coords.accuracy;
          devLog(`✅ GetUserLocationInfo: 獲得位置: 緯度=${lat}, 經度=${lng}, 精確度=${accuracy}m`);
          geocodeCoordinates(lat, lng).then(resolve);
        } else {
          devLog("🏠 GetUserLocationInfo: 位置獲取失敗，使用永靖預設位置");
          const defaultLocation = getYongJingDefaultLocation();
          geocodeCoordinates(defaultLocation.lat, defaultLocation.lng).then(resolve);
        }
      })
      .catch((error) => {
        console.error("❌ GetUserLocationInfo: 位置存取錯誤:", error);
        const defaultLocation = getYongJingDefaultLocation();
        geocodeCoordinates(defaultLocation.lat, defaultLocation.lng).then(resolve);
      });
  });

  const hardTimeout = new Promise<null>((resolve) => setTimeout(() => {
    devLog("⏰ GetUserLocationInfo: 超過 4 秒硬性上限，回傳 null");
    resolve(null);
  }, HARD_TIMEOUT_MS));

  return Promise.race([locationWork, hardTimeout]);
};

export const ProcessCurrentUserData = async () => {
  const mapContext = useContext(MapInfoContext);

  devLog("🧠 ProcessCurrentUserData: 使用自動定位系統");

  // 檢查瀏覽器支援
  if (!checkGeolocationSupport()) {
    devLog("Geolocation is not supported by this browser, using default coordinates for map context.");
    const defaultLocation = getYongJingDefaultLocation();
    mapContext.setInMyLocation(defaultLocation.lat, defaultLocation.lng);
    return;
  }

  try {
    devLog(`🧠 ProcessCurrentUserData: 使用智能位置獲取 (目標: ${LOCATION_ACCURACY_CONFIG.TARGET_ACCURACY}m)`);
    const position = await getSmartUserLocation(LOCATION_ACCURACY_CONFIG.TARGET_ACCURACY);
    if (position) {
      // 定位成功，使用使用者實際位置
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      const accuracy = position.coords.accuracy;
      devLog(`✅ ProcessCurrentUserData: 設定地圖上下文為位置: 緯度=${lat}, 經度=${lng}, 精確度=${accuracy}m`);
      mapContext.setInMyLocation(lat, lng);
    } else {
      // 位置獲取失敗，使用永靖預設座標
      devLog("🏠 ProcessCurrentUserData: 位置獲取失敗，使用永靖預設位置");
      const defaultLocation = getYongJingDefaultLocation();
      mapContext.setInMyLocation(defaultLocation.lat, defaultLocation.lng);
    }
  } catch (error) {
    console.error("❌ ProcessCurrentUserData: 位置獲取錯誤:", error);
    devLog("🏠 使用永靖預設座標作為備援");
    const defaultLocation = getYongJingDefaultLocation();
    mapContext.setInMyLocation(defaultLocation.lat, defaultLocation.lng);
  }
};

export const ProcessMapData = (mapContext: any, attractionData: AttractionData[], isDrawPath: boolean) => {

  // devLog("[ProcessMapData]");
  const tmp: { lat: number; lng: number; iconUrl: string; }[] = [];


  // mapContext.isDrawPath = isDrawPath;

  if (attractionData == null || attractionData == undefined)
    return;

  // devLog("[mapLib] attractions ="+attractionData.length);
  mapContext.setInAttractions(attractionData);
  mapContext.setInDrawPath(isDrawPath);

  for (let i = 0; i < attractionData.length; i++) {

    const val = attractionData[i];

    switch (val.maker_type) {
      case "attraction":

        tmp.push({ lat: parseFloat(val.latitude), lng: parseFloat(val.longitude), iconUrl: val.icon_slug != null ? val.icon_slug : "https://storage.googleapis.com/yongjing-storge/default/map_icon/attraction.png" })
        break;
      case "temple":

        tmp.push({ lat: parseFloat(val.latitude), lng: parseFloat(val.longitude), iconUrl: val.icon_slug != null ? val.icon_slug : "https://storage.googleapis.com/yongjing-storge/default/map_icon/temple.png" })
        break;
      case "food":
        tmp.push({ lat: parseFloat(val.latitude), lng: parseFloat(val.longitude), iconUrl: val.icon_slug != null ? val.icon_slug : "https://storage.googleapis.com/yongjing-storge/default/map_icon/food.png" })
        break;
      case "monument":
        tmp.push({ lat: parseFloat(val.latitude), lng: parseFloat(val.longitude), iconUrl: val.icon_slug != null ? val.icon_slug : "https://storage.googleapis.com/yongjing-storge/default/map_icon/monument.png" })
        break;
      case "hotel":
        tmp.push({ lat: parseFloat(val.latitude), lng: parseFloat(val.longitude), iconUrl: val.icon_slug != null ? val.icon_slug : "https://storage.googleapis.com/yongjing-storge/default/map_icon/hotel.png" })
        break;
    }
  }

  mapContext.setInMarkers(tmp);
  mapContext.setInIsUpdate(true);

}

export const ResetMapData = (mapContext: any) => {
  mapContext.attraction = [];
  mapContext.isDrawPath = false;
  mapContext.markers = [];
}
